import { ref, onMounted, watch, nextTick } from 'vue';
import type { FilterConfiguration } from '@/components/base/dynamicFilterBuilder';
import { useI18n } from '@/composables/useI18n';
import { isAbortError, useAbortController } from "@/composables/useAbortController";
import {
  DEFAULT_SORT_COLUMN_FETCH_ISSUERS,
  fetchIssuers,
} from "@/components/issuers/scripts";
import type {
  FetchIssuersParam,
  Issuer
} from "@/components/issuers/scripts";
import {loadIssuerFromStorageOrFirst} from "@/components/issuers/scripts/issuers/helper";

/**
 * Options for configuring the issuer filter.
 */
interface IssuerFilterOptions {
  /**
   * Callback function to be called when the issuer is initialized after the default issuer is loaded.
   * @param issuer - The initialized issuer or null.
   */
  onInitialized?: (issuer: Issuer | null) => void;

  /**
   * Callback function to be called when the issuer filter is reloaded.
   * issuer filter reload requires initializeWithIssuerFromStorage to be true
   * @param issuer - The reloaded issuer or null.
   */
  onReload?: (issuer: Issuer | null) => void;

  /**
   * Callback function to be called when an error occurs.
   * @param error - The error object.
   */
  onError?: (error: unknown) => void;

  /**
   * Whether to initialize the issuer filter with the issuer saved in local storage or the first issuer.
   * If false or undefined, the filter will start empty.
   */
  initializeWithIssuerFromStorage?: boolean;

  /**
   * Optional entity ID from a route param. When provided and > 0, overrides localStorage lookup
   * and loads the issuer directly by this ID.
   * Can be a plain value or a getter function for reactive use (e.g., from route.params.id).
   */
  entityId?: (() => number | null) | number | null;

  /**
   * Whether to preload the issuer list on mount by calling getFilteredItems with no search input.
   * Useful when initializeWithIssuerFromStorage is false but you still want items ready in the dropdown.
   */
  preloadItems?: boolean;
}

/**
 * Composable for creating and managing an issuer filter.
 * The filter component can be initialized with the default issuer from storage or the first issuer found on the server.
 * The filter configuration needs to be used with the DynamicFilterBuilder component.
 * @param options - Optional configuration options for the issuer filter.
 * @returns An object containing the issuer filter configuration and the selected issuer.
 */
export const useIssuerFilter = (options?: IssuerFilterOptions) => {
  const { t } = useI18n();
  const { getNewAbortController } = useAbortController();
  const selectedIssuer = ref<Issuer | null>(null); // model used to initialize the value of the filter
  const preloadRefreshTrigger = ref(0);

  const resolveEntityId = (): number | null | undefined => {
    if (typeof options?.entityId === 'function') return options.entityId();
    return options?.entityId ?? undefined;
  };

  const getFilteredItems = async (searchInput: string | null) => {
    const fetchArgs: FetchIssuersParam = {
      sortColumn: DEFAULT_SORT_COLUMN_FETCH_ISSUERS
    };
    if (searchInput) fetchArgs.companyName = searchInput;

    const { error, response } = await fetchIssuers(fetchArgs, getNewAbortController().signal);

    if (error) {
      options?.onError?.(error);
    }

    return response || [];
  }

  /**
   * Creates a filter configuration for selecting issuers.
   * @param config - Configuration for the filter including column properties, events, and model.
   * @returns The filter configuration object.
   */
  const createIssuerFilterConfiguration = (
    config?: Pick<FilterConfiguration, 'colProps' | 'events' | "debounce" | "props">
  ): FilterConfiguration => {
    return {
      ...(config ?? {}),
      type: "combobox",
      props: {
        label: t("IssuerCoreData.issuer_selection"),
        "item-text": "companyName",
        placeholder: t("IssuerCoreData.issuer_selection"),
        ...config?.props ?? {},
      },
      model: selectedIssuer,
      getFilteredItems: getFilteredItems,
      ...(options?.preloadItems ? { refreshTrigger: preloadRefreshTrigger } : {}),
    };
  };

  onMounted(async () => {
    if (options?.initializeWithIssuerFromStorage === true) {
      try {
        const { response, error } = await loadIssuerFromStorageOrFirst(resolveEntityId());
        if (error) {
          options?.onError?.(error);
          return;
        }
        if (response) {
          // set initial value
          selectedIssuer.value = response;
          options?.onInitialized?.(response);
        }
      } catch (error) {
        if (!isAbortError(error)) {
          options?.onError?.(error);
        }
      }
    }

    if (options?.preloadItems) {
      // nextTick ensures DynamicFilterBuilder has mounted and set up its refreshTrigger watch
      await nextTick();
      preloadRefreshTrigger.value++;
    }
  });

  const triggerFilterReload = async () => {
    if (options?.initializeWithIssuerFromStorage === true) {
      try {
        const { response, error } = await loadIssuerFromStorageOrFirst(resolveEntityId());
        if (error) {
          options?.onError?.(error);
          return;
        }
        if (response) {
          // set reload value
          selectedIssuer.value = response;
          options?.onReload?.(response);
        }
      } catch (error) {
        if (!isAbortError(error)) {
          options?.onError?.(error);
        }
      }
    } else {
      console.warn("Issuer filter reload requires initializeWithIssuerFromStorage to be true. Reload skipped.");
    }
  }

  // Watch for reactive entityId changes (e.g., from route.params.id via computed)
  if (typeof options?.entityId === 'function') {
    watch(options.entityId, async (newId, oldId) => {
      if (newId === oldId) return;
      // Skip if the currently displayed entity already matches the new ID.
      // This happens when the URL changed as a result of the user's own combobox selection.
      if (newId != null && selectedIssuer.value?.id === newId) return;
      if (options?.initializeWithIssuerFromStorage !== true) return;
      try {
        const { response, error } = await loadIssuerFromStorageOrFirst(resolveEntityId());
        if (error) { options?.onError?.(error); return; }
        if (response) {
          selectedIssuer.value = response;
          // Prefer onReload; fall back to onInitialized for components that only define onInitialized
          if (options?.onReload) {
            options.onReload(response);
          } else {
            options?.onInitialized?.(response);
          }
        }
      } catch (error) {
        if (!isAbortError(error)) options?.onError?.(error);
      }
    });
  }

  const clearFilter = () => {
    selectedIssuer.value = null;
  };

  return {
    createIssuerFilterConfiguration,
    triggerFilterReload,
    clearFilter,
  };
};
